// Every character in the current script, grouped by team with a one-line summary. Visible to the
// whole table on purpose: the script is public knowledge, so this is a reference sheet, not a leak.
// What is *not* here is who holds what — that comes from the Night Order panel, Storytellers only.

import { TEAM_ORDER } from "@cc/shared";
import type { GameView, Team } from "@cc/shared";
import { teamColor, teamLabel } from "../../lib/ui.js";
import { Panel } from "./Panel.js";

export function RolesInPlay({
  view,
  open,
  onOpenChange,
}: {
  view: GameView;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const byTeam = new Map<Team | null, typeof view.script.characters>();
  for (const character of view.script.characters) {
    const list = byTeam.get(character.team) ?? [];
    list.push(character);
    byTeam.set(character.team, list);
  }
  const teams = [...TEAM_ORDER].filter((t) => byTeam.has(t));

  return (
    <Panel
      title="Roles in Play"
      badge={<span className="text-sm text-parchment-dim">{view.script.name}</span>}
      open={open}
      onOpenChange={onOpenChange}
    >
      {view.script.characters.length === 0 ? (
        <p className="py-2 text-sm text-parchment-dim">No script loaded yet.</p>
      ) : (
        teams.map((team) => (
          <div key={team} className="mb-3 last:mb-0">
            <h3 className="mb-1.5 text-sm font-semibold uppercase tracking-wide" style={{ color: teamColor(team) }}>
              {teamLabel(team)}
            </h3>
            <ul className="space-y-1.5">
              {(byTeam.get(team) ?? []).map((character) => (
                <li key={character.id} className="flex gap-2">
                  {character.icon ? (
                    <img src={character.icon} alt="" className="mt-0.5 size-9 shrink-0 object-contain" />
                  ) : (
                    <span className="mt-0.5 size-9 shrink-0" />
                  )}
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-parchment">{character.name}</div>
                    {character.ability && (
                      <div className="text-xs leading-snug text-parchment-dim">{character.ability}</div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))
      )}
    </Panel>
  );
}
